import { Field, ErrorMessage, FormikErrors, FormikTouched } from 'formik';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { TranslatableFieldLabel } from '@/components/composite/TranslatableFieldLabel';
import { isContentLocaleEnabled } from '@/lib/platform-filters';
import { QuestionFormValues } from '@/utils/forms/questionTransformers';

interface OptionTextFieldsProps {
  index: number;
  optionTextEn: string;
  optionTextFr: string;
  errors?: FormikErrors<QuestionFormValues['options'][number]>;
  touched?: FormikTouched<QuestionFormValues['options'][number]>;
  translateField: (
    sourceKey: string,
    sourceValue: string,
    targetKey: string,
    setFieldValue: any
  ) => void;
  isFieldTranslating: (key: string) => boolean;
  setFieldValue: (field: string, value: any) => void;
  isEditable: boolean;
}

export function OptionTextFields({
  index,
  optionTextEn,
  errors,
  touched,
  translateField,
  isFieldTranslating,
  setFieldValue,
  isEditable,
}: OptionTextFieldsProps) {
  // Whether this instance authors Option text in a second Content Locale (CAE-762).
  const hasSecondaryContentLocale = isContentLocaleEnabled('fr');

  return (
    <div className={hasSecondaryContentLocale ? 'grid grid-cols-2 gap-4' : 'flex flex-col gap-2'}>
      <div className="flex flex-col gap-2">
        {hasSecondaryContentLocale ? (
          <TranslatableFieldLabel
            htmlFor={`options.${index}.option_text_en`}
            onTranslate={() =>
              translateField(
                `option_${index}_text_en`,
                optionTextEn,
                `options.${index}.option_text_fr`,
                setFieldValue
              )
            }
            isTranslating={isFieldTranslating(`option_${index}_text_en`)}
          >
            Option Text (English)
          </TranslatableFieldLabel>
        ) : (
          <Label htmlFor={`options.${index}.option_text_en`}>Option Text</Label>
        )}
        <Field
          as={Input}
          name={`options.${index}.option_text_en`}
          data-testid={`question-option-${index}-option_text_en`}
          placeholder="Enter option text"
          disabled={!isEditable}
          className={errors?.option_text_en && touched?.option_text_en ? 'border-red-500' : ''}
        />
        <ErrorMessage name={`options.${index}.option_text_en`}>
          {msg => <span className="text-sm text-red-500">{msg}</span>}
        </ErrorMessage>
      </div>

      {hasSecondaryContentLocale && (
        <div className="flex flex-col gap-2">
          <Label>Option Text (Spanish)</Label>
          <Field
            as={Input}
            name={`options.${index}.option_text_fr`}
            data-testid={`question-option-${index}-option_text_fr`}
            placeholder="Enter option text"
            disabled={!isEditable}
            className={errors?.option_text_fr && touched?.option_text_fr ? 'border-red-500' : ''}
          />
          <ErrorMessage name={`options.${index}.option_text_fr`}>
            {msg => <span className="text-sm text-red-500">{msg}</span>}
          </ErrorMessage>
        </div>
      )}
    </div>
  );
}
